import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Calendar, CreditCard, CheckCircle2 } from "lucide-react";
import type { Account } from "@/hooks/useAccounts";
import { getErrorMessage } from "@/lib/errors";

interface AutopaySetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: Account[];
  currentBalance: number;
  minimumPayment: number;
  nextDueDate: string | null;
  onConfigure: (
    enabled: boolean,
    sourceAccountType: string,
    amountType: string,
    customAmount?: number
  ) => Promise<void>;
}

const AMOUNT_TYPES = [
  { value: "minimum", label: "Minimum Payment", desc: "Only the minimum amount due each month" },
  { value: "full", label: "Full Balance", desc: "Pay the entire balance — avoids all interest" },
  { value: "statement", label: "Statement Balance", desc: "Pay the statement balance in full" },
  { value: "custom", label: "Custom Amount", desc: "Set a fixed dollar amount each month" },
];

const AutopaySetupModal = ({
  open, onOpenChange, accounts, currentBalance, minimumPayment, nextDueDate, onConfigure,
}: AutopaySetupModalProps) => {
  const [sourceType, setSourceType] = useState("checking");
  const [amountType, setAmountType] = useState("minimum");
  const [customAmount, setCustomAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<"setup" | "confirm">("setup");

  const eligibleAccounts = accounts.filter(
    (a) => ["checking", "savings"].includes(a.account_type) && a.status === "active"
  );

  const selectedAccount = eligibleAccounts.find((a) => a.account_type === sourceType);

  const getPaymentPreview = () => {
    switch (amountType) {
      case "minimum": return `$${minimumPayment || 25}`;
      case "full": return `$${currentBalance}`;
      case "statement": return `$${currentBalance}`;
      case "custom": return `$${parseInt(customAmount) || 0}`;
      default: return "$0";
    }
  };

  const isValid = () => {
    if (!selectedAccount) return false;
    if (amountType === "custom") {
      const amt = parseInt(customAmount);
      return amt > 0;
    }
    return true;
  };

  const handleContinue = () => {
    if (!isValid()) return;
    setStep("confirm");
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfigure(
        true,
        sourceType,
        amountType,
        amountType === "custom" ? parseInt(customAmount) : undefined
      );
      toast.success("Autopay enabled successfully!");
      setStep("setup");
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(getErrorMessage(e) || "Failed to enable autopay");
    } finally {
      setSaving(false);
    }
  };

  const dueLabel = nextDueDate
    ? new Date(nextDueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "30 days from now";

  if (step === "confirm") {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) setStep("setup"); onOpenChange(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Confirm Autopay
            </DialogTitle>
            <DialogDescription>Review your autopay settings before enabling.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 rounded-xl border border-border/60 bg-muted/30 p-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Source</span>
              <span className="font-semibold text-foreground">{selectedAccount?.account_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Type</span>
              <span className="font-semibold text-foreground capitalize">{amountType === "custom" ? "Custom" : AMOUNT_TYPES.find(t => t.value === amountType)?.label}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-heading font-bold text-foreground">{getPaymentPreview()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Next Payment</span>
              <span className="font-semibold text-foreground">{dueLabel}</span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
            💡 Autopay will deduct from your {selectedAccount?.account_name} on each due date. If funds are insufficient, the payment will fail and may impact your credit score.
          </p>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setStep("setup")}>
              Back
            </Button>
            <Button className="flex-1" onClick={handleConfirm} disabled={saving}>
              {saving ? "Enabling..." : "Enable Autopay"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Set Up Autopay
          </DialogTitle>
          <DialogDescription>
            Automatically pay your credit card bill each month — just like a real bank.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Source Account */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Pay From</Label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {eligibleAccounts.map((a) => (
                  <SelectItem key={a.account_type} value={a.account_type}>
                    {a.account_name} — ${a.balance.toLocaleString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {eligibleAccounts.length === 0 && (
              <p className="text-xs text-destructive">No active accounts available. Unlock Checking or Savings first.</p>
            )}
          </div>

          {/* Amount Type */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Payment Amount</Label>
            <RadioGroup value={amountType} onValueChange={setAmountType} className="space-y-2">
              {AMOUNT_TYPES.map((t) => (
                <label
                  key={t.value}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    amountType === t.value ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/30"
                  }`}
                >
                  <RadioGroupItem value={t.value} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.desc}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {amountType === "custom" && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Custom Amount ($)</Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                min={1}
              />
            </div>
          )}

          {/* Due Date */}
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Next payment date: <strong className="text-foreground">{dueLabel}</strong>
            </span>
          </div>

          <Button onClick={handleContinue} disabled={!isValid()} className="w-full">
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AutopaySetupModal;
